import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { BLOCKS, getLessonsByBlock } from '../data/curriculum';
import { ChevronLeft, Lock, CheckCircle2, PlayCircle, Sparkles, Eye, Link2, PlusCircle, Brain } from 'lucide-react';

const ProgressPage = ({ progress, isLessonUnlocked, isLessonCompleted, getLessonAccuracy }) => {
  const navigate = useNavigate();

  const getBlockIcon = (iconName) => {
    switch (iconName) {
      case 'sparkles': return <Sparkles className="w-5 h-5" />;
      case 'eye': return <Eye className="w-5 h-5" />;
      case 'link': return <Link2 className="w-5 h-5" />;
      case 'plus-minus': return <PlusCircle className="w-5 h-5" />;
      case 'brain': return <Brain className="w-5 h-5" />;
      default: return <Sparkles className="w-5 h-5" />;
    }
  };

  const calculateBlockProgress = (blockId) => {
    const lessons = getLessonsByBlock(blockId);
    const completed = lessons.filter(l => isLessonCompleted(l.id)).length;
    return Math.round((completed / lessons.length) * 100);
  };

  return (
    <div className="app-container min-h-screen bg-background safe-area-top safe-area-bottom">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="sticky top-0 bg-background/95 backdrop-blur-sm px-4 py-4 border-b border-border z-10"
      >
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/home')}
            className="w-10 h-10 rounded-full"
          >
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-xl font-display font-bold text-foreground">Все уроки</h1>
            <p className="text-sm text-muted-foreground">Прогресс {progress.childName}</p>
          </div>
        </div>
      </motion.div>

      {/* Blocks list */}
      <div className="px-4 py-6 space-y-6">
        {BLOCKS.map((block, blockIndex) => {
          const lessons = getLessonsByBlock(block.id);
          const blockProgress = calculateBlockProgress(block.id);
          const isBlockUnlocked = block.id === 1 || calculateBlockProgress(block.id - 1) >= 100;

          return (
            <motion.div
              key={block.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: blockIndex * 0.1 }}
              className={`rounded-2xl border ${isBlockUnlocked ? 'border-border bg-card' : 'border-border/50 bg-muted/30'}`}
            >
              {/* Block header */}
              <div className="p-4 border-b border-border">
                <div className="flex items-center gap-3">
                  <div 
                    className={`w-10 h-10 rounded-xl flex items-center justify-center ${isBlockUnlocked ? '' : 'opacity-50'}`}
                    style={{ background: isBlockUnlocked ? `hsl(var(--${block.color}))` : undefined }}
                  >
                    {isBlockUnlocked ? (
                      <span className="text-card">{getBlockIcon(block.icon)}</span>
                    ) : (
                      <Lock className="w-5 h-5 text-muted-foreground" />
                    )}
                  </div>
                  <div className="flex-1">
                    <h2 className={`font-display font-bold ${isBlockUnlocked ? 'text-foreground' : 'text-muted-foreground'}`}>
                      Блок {block.id}: {block.title}
                    </h2>
                    <p className="text-sm text-muted-foreground">
                      {block.description}
                    </p>
                  </div>
                </div>

                {/* Block progress */}
                {isBlockUnlocked && (
                  <div className="mt-3">
                    <div className="flex justify-between text-xs text-muted-foreground mb-1">
                      <span>Прогресс</span>
                      <span>{blockProgress}%</span>
                    </div>
                    <Progress value={blockProgress} className="h-1.5" />
                  </div>
                )}
              </div>

              {/* Lessons list */}
              {isBlockUnlocked && (
                <div className="p-3 space-y-2">
                  {lessons.map((lesson) => {
                    const unlocked = isLessonUnlocked(lesson.id);
                    const completed = isLessonCompleted(lesson.id);
                    const accuracy = getLessonAccuracy(lesson.id);
                    const isCurrentLesson = lesson.id === progress.currentLessonId;

                    return (
                      <motion.button
                        key={lesson.id}
                        whileTap={unlocked ? { scale: 0.98 } : undefined}
                        onClick={() => unlocked && navigate(`/lesson/${lesson.id}`)}
                        disabled={!unlocked}
                        className={`w-full p-3 rounded-xl flex items-center gap-3 transition-all ${
                          isCurrentLesson
                            ? 'bg-primary-light border-2 border-primary'
                            : completed
                            ? 'bg-correct-light'
                            : unlocked
                            ? 'bg-background hover:bg-muted'
                            : 'opacity-50 cursor-not-allowed'
                        }`}
                      >
                        {/* Status icon */}
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          completed
                            ? 'bg-correct text-card'
                            : isCurrentLesson
                            ? 'bg-primary text-primary-foreground'
                            : unlocked
                            ? 'bg-muted text-muted-foreground'
                            : 'bg-muted/50 text-muted-foreground'
                        }`}>
                          {completed ? (
                            <CheckCircle2 className="w-4 h-4" />
                          ) : isCurrentLesson ? (
                            <PlayCircle className="w-4 h-4" />
                          ) : unlocked ? (
                            <span className="text-xs font-bold">{lesson.id}</span>
                          ) : (
                            <Lock className="w-3 h-3" />
                          )}
                        </div>

                        {/* Lesson info */}
                        <div className="flex-1 text-left">
                          <p className={`font-medium text-sm ${
                            unlocked ? 'text-foreground' : 'text-muted-foreground'
                          }`}>
                            {lesson.title}
                          </p>
                          {completed && accuracy > 0 && (
                            <p className="text-xs text-success">
                              Лучший результат: {accuracy}%
                            </p>
                          )}
                        </div>

                        {/* Current indicator */}
                        {isCurrentLesson && !completed && (
                          <span className="text-xs px-2 py-1 rounded-full bg-primary text-primary-foreground font-medium">
                            Текущий
                          </span>
                        )}
                      </motion.button>
                    );
                  })}
                </div>
              )}
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};

export default ProgressPage;
